import {
  addYears,
  differenceInMonths,
  differenceInYears,
  isBefore,
  setYear,
  startOfDay,
} from 'date-fns';

import { parseCalendarDate } from '../date.ts';

/**
 * Defines what qualifies as "upcoming" birthday
 */
//TODO: Should probably be coming from SDG at some point
export const UPCOMING_BIRTHDAY_MONTHS_LOOKBACK = 3;

export const getAgeFromBirthday = (birthDate: string) => {
  const parsed = parseCalendarDate(birthDate);

  if (!parsed) {
    return '';
  }

  return differenceInYears(new Date(), parsed);
};

export const monthsUntilBirthday = (birthDate: string) => {
  const parsed = parseCalendarDate(birthDate);

  if (!parsed) {
    return null;
  }

  const today = startOfDay(new Date());
  let nextBirthday = setYear(parsed, today.getFullYear());

  // If birthday has passed this year, use next year
  if (isBefore(nextBirthday, today)) {
    nextBirthday = addYears(nextBirthday, 1);
  }

  return differenceInMonths(nextBirthday, today) + 1; // +1 to count the current month if we're within it
};

const birthdateMonthDayPattern = /^(\d{4})-(\d{2})-(\d{2})/;
const msPerDay = 24 * 60 * 60 * 1000;
const daysInMonthByIndex = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const isLeapYear = (year: number): boolean =>
  year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);

const parseBirthdateMonthDay = (
  birthdate: string,
): { monthIndex: number; day: number } | null => {
  const match = birthdateMonthDayPattern.exec(birthdate);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const daysInMonth =
    monthIndex === 1 && isLeapYear(year) ? 29 : daysInMonthByIndex[monthIndex];

  if (!daysInMonth || day < 1 || day > daysInMonth) {
    return null;
  }

  return { monthIndex, day };
};

/**
 * Number of days from `today` (inclusive) until the next occurrence of
 * `birthdate`'s month/day. Returns 0 when the birthday is today.
 *
 * Parses the YYYY-MM-DD prefix directly, then uses UTC math to avoid time-zone
 * drift when sorting cards across midnight.
 */
const daysUntilNextBirthday = (
  birthdate: string,
  startDate = new Date(),
): number => {
  const birthday = parseBirthdateMonthDay(birthdate);
  if (!birthday) {
    return Number.POSITIVE_INFINITY;
  }

  const startYear = startDate.getUTCFullYear();
  const todayUtc = Date.UTC(
    startYear,
    startDate.getUTCMonth(),
    startDate.getUTCDate(),
  );

  let nextUtc = Date.UTC(startYear, birthday.monthIndex, birthday.day);

  if (nextUtc < todayUtc) {
    nextUtc = Date.UTC(startYear + 1, birthday.monthIndex, birthday.day);
  }

  return (nextUtc - todayUtc) / msPerDay;
};

/**
 * Comparator for sorting children by which birthday comes next.
 * Missing or invalid birthdates sort to the end.
 */
export const compareUpcomingBirthday = (
  a: string | null | undefined,
  b: string | null | undefined,
  startDate = new Date(),
): number => {
  const aDays = a
    ? daysUntilNextBirthday(a, startDate)
    : Number.POSITIVE_INFINITY;
  const bDays = b
    ? daysUntilNextBirthday(b, startDate)
    : Number.POSITIVE_INFINITY;

  return aDays === bDays ? 0 : aDays - bDays;
};
